import AXIOS from 'axios';

/**
 * Upload Service API Client
 *
 * This module provides axios configuration for the secure upload service
 * (uploader.projector.app) that uses presigned URLs instead of exposing
 * AWS credentials in the client.
 */

// Lazy import to avoid circular dependency
// getUploaderToken is imported at request time, not module load time
let getUploaderTokenFn = null;
const getUploaderToken = async () => {
  if (!getUploaderTokenFn) {
    // Dynamic import to break circular dependency
    const tokenManager = await import('./tokenManager');
    getUploaderTokenFn = tokenManager.getUploaderToken;
  }
  return getUploaderTokenFn();
};

// Environment-based URL selection
const PRODUCTION_UPLOADER = 'https://uploader.projector.app/api';
const PREPROD_UPLOADER = 'https://uploader.preprod.projector.app/api';

// Always use production upload service for all environments (matches mobile app)
const uploaderBaseUrl = PRODUCTION_UPLOADER;

/*
// Commented out - Environment-specific routing (can be restored if needed)
const getUploaderBaseUrl = () => {
  if (window.location.origin.includes('localhost')) {
    return PRODUCTION_UPLOADER;
  }
  if (window.location.origin.includes('preprod')) {
    return PREPROD_UPLOADER;
  }
  if (window.location.origin.includes('dev.app')) {
    return PREPROD_UPLOADER;
  }
  return PRODUCTION_UPLOADER;
};
const uploaderBaseUrl = getUploaderBaseUrl();
*/

console.log('[UploaderAPI] Using base URL:', uploaderBaseUrl);

// Create axios instance for upload service
const uploaderAxios = AXIOS.create({
  baseURL: uploaderBaseUrl,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout
});

// Request interceptor to inject uploader JWT token
uploaderAxios.interceptors.request.use(
  async (req) => {
    try {
      // Get valid uploader token (JWT format)
      const token = await getUploaderToken();

      // Add token to Authorization header (Bearer token)
      if (token) {
        req.headers.Authorization = `Bearer ${token}`;
        console.log('[UploaderAPI] Token attached to request');
      } else {
        console.warn('[UploaderAPI] No uploader token available');
      }

      return req;
    } catch (error) {
      console.error('[UploaderAPI] Failed to get uploader token:', error);
      // Continue without token - backend will reject if auth is required
      return req;
    }
  },
  (error) => {
    console.error('[UploaderAPI] Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
uploaderAxios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      // Server responded with error status
      console.error('[UploaderAPI] Response error:', {
        status: error.response.status,
        data: error.response.data,
        url: error.config?.url,
      });
    } else if (error.request) {
      // Request was made but no response received
      console.error('[UploaderAPI] Network error: No response received');
    } else {
      // Error in request setup
      console.error('[UploaderAPI] Request setup error:', error.message);
    }
    return Promise.reject(error);
  }
);

// Axios instance for gift uploads (no auth token - uses gift_uuid instead)
const giftUploaderAxios = AXIOS.create({
  baseURL: uploaderBaseUrl,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Response interceptor for error handling (same as uploaderAxios)
giftUploaderAxios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      console.error('[GiftUploaderAPI] Response error:', {
        status: error.response.status,
        data: error.response.data,
        url: error.config?.url,
      });
    } else if (error.request) {
      console.error('[GiftUploaderAPI] Network error: No response received');
    } else {
      console.error('[GiftUploaderAPI] Request setup error:', error.message);
    }
    return Promise.reject(error);
  }
);

export { uploaderAxios, uploaderBaseUrl, giftUploaderAxios };
